import { memo, useMemo, useState } from 'react';

import { type Clip } from '@air/api/types';
import { itemIsHighlightedOnShiftSelectSelector } from '@air/redux-selected-items';

import { type AssetItemProps } from '~/components/Gallery/types';
import { AssetCellThumbnail } from '~/components/TableView/NameCell/AssetCellThumbnail';
import { NameCellContent } from '~/components/TableView/NameCell/NameCellContent';
import { DropdownMenuWrapper } from '~/components/TableView/PrivateTableView/DropdownMenuWrapper';
import { PrivateAssetNameCellTitle } from '~/components/TableView/PrivateTableView/PrivateAssetNameCellTitle';
import { ASSET_NAME_CELL } from '~/constants/testIDs';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useIsItemRenaming } from './TableViewItemRenamingContext';
import { useEscapeKeyHandler } from './useEscapeKeyHandler';

export type PrivateAssetNameCellProps = {
  isHovering: boolean;
  isSelected: boolean;
  clip: Clip;
} & AssetItemProps;

export const PrivateAssetNameCell = memo(
  ({
    isHovering,
    isSelected,
    clip,
    getItemMenuOptions,
    getSelectionMenuOptions,
    onCommentsClick,
    onItemClick,
    onItemCmdClick,
    onVersionsClick,
  }: PrivateAssetNameCellProps) => {
    const itemTitle = clip.title || clip.importedName || 'Untitled';
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const isHighlightedFromShiftSelect = useAirSelector((st) => itemIsHighlightedOnShiftSelectSelector(st, clip.id));
    const isCurrentlyRenaming = useIsItemRenaming(clip.id);

    const _title = useMemo(() => {
      return (
        <PrivateAssetNameCellTitle
          id={clip.id}
          assetId={clip.assetId}
          title={itemTitle}
          onClick={onItemClick}
          onCmdClick={onItemCmdClick}
          onCommentsClick={onCommentsClick}
          onVersionsClick={onVersionsClick}
          openCommentCount={clip.openCommentCount || 0}
          assetVersionCount={clip.assetVersionCount || 0}
          pagesCount={clip.pages || 0}
        />
      );
    }, [
      clip.pages,
      clip.assetId,
      clip.assetVersionCount,
      clip.id,
      clip.openCommentCount,
      itemTitle,
      onCommentsClick,
      onItemClick,
      onItemCmdClick,
      onVersionsClick,
    ]);

    const _image = useMemo(() => {
      return <AssetCellThumbnail clip={clip} description={itemTitle} />;
    }, [clip, itemTitle]);

    const ellipsisMenuOptions = useMemo(
      () => getItemMenuOptions({ trackLocation: 'table-item-ellipsis-menu' }),
      [getItemMenuOptions],
    );

    const selectionMenuOptions = getSelectionMenuOptions('table-multi-select-ellipsis-menu');
    const menuOptions = isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions;

    const shouldShowMenu = useMemo(() => {
      if (isCurrentlyRenaming === true) {
        return false;
      }

      if (isHighlightedFromShiftSelect === false) {
        if (isHovering === true || menuIsOpen === true) {
          return true;
        }
      }

      return false;
    }, [isCurrentlyRenaming, isHighlightedFromShiftSelect, isHovering, menuIsOpen]);

    const onEscapeKeyDown = useEscapeKeyHandler(shouldShowMenu, isSelected, menuIsOpen, clip);

    return (
      <NameCellContent data-testid={ASSET_NAME_CELL} title={_title} image={_image}>
        <div className="flex items-center gap-1">
          {shouldShowMenu && !!menuOptions.length && (
            <DropdownMenuWrapper
              options={menuOptions}
              onOpenChange={setMenuIsOpen}
              onEscapeKeyDown={onEscapeKeyDown}
              dataTestId={`${ASSET_NAME_CELL}-menu`}
            />
          )}
        </div>
      </NameCellContent>
    );
  },
);

PrivateAssetNameCell.displayName = 'PrivateAssetNameCell';
